'use client'

import Link from 'next/link'
import AppIcon from '@/components/content/app/AppIcon'
import Marquee from '@/components/ui/Marquee'
import { determineDealsToDisplay } from '@/components/content/deals/helpers'
import { useChatContext } from '@/context/ChatContext'
import { cn, extractPrimaryCategory } from '@/lib/utils'
import { FeaturedPlacement, trackFeaturedClick } from '@/lib/analytics'
import { PREFETCH } from '@/lib/prefetch'
import { type App, type Deal } from '@/payload-types'
import { Icon } from '@/icons/Icon'

import type { ResultItemApp } from '@/types/app'

type AppProps = ResultItemApp

type DockAppProps = AppProps & Pick<App, 'slug' | 'featuredOrder'>

type ChatAppsProps = {
  apps: DockAppProps[]
}

type AppCardProps = {
  app?: AppProps
  active?: boolean
  suggestion?: string | null
  condensed?: boolean
}

export default function ChatApps({ apps }: ChatAppsProps) {
  const featuredApps = apps.filter(
    (app): app is DockAppProps => app.featuredOrder !== null && app.featuredOrder !== undefined,
  )

  if (featuredApps.length === 0) return null

  return (
    <Marquee className="z-10 my-10 py-8 md:mt-16" duration={180} pauseOnHover={true}>
      <div className="flex w-fit items-center gap-4 pl-4">
        {featuredApps.map((app) => (
          <DockCard key={app.id} app={app} />
        ))}
      </div>
    </Marquee>
  )
}

function DockCard({ app }: { app: DockAppProps }) {
  const dealCount = determineDealsToDisplay((app.deals?.docs ?? []) as Deal[]).length
  return (
    <Link
      href={`/apps/${app.slug}`}
      prefetch={PREFETCH}
      onClick={() => trackFeaturedClick(FeaturedPlacement.HOME_PILLS, app.name)}
      className="group flex h-14 w-56 shrink cursor-pointer items-center gap-3 truncate rounded-2xl border border-black/10 bg-white/75 pl-2 pr-2.5 transition-all hover:border-primary-200 hover:shadow-[0px_0px_0px_3px_rgba(255,240,242,1.00)]"
    >
      <AppIcon
        icon={app.icon}
        glyph={app.glyph}
        name={app.name}
        background={app.brandBackgroundColor}
        size={40}
      />
      <div className="flex w-full flex-col gap-1 truncate">
        <span className="truncate text-14 font-medium leading-tight text-gray-950">{app.name}</span>
        <span className="h-full text-12 leading-tight text-gray-500">
          App review, {dealCount} deal
          {dealCount === 1 ? '' : 's'}
        </span>
      </div>
      <div className="relative h-full w-5">
        <div className="absolute right-0 top-2 text-primary-500">
          <Icon name="best" />
        </div>
      </div>
    </Link>
  )
}

export function AppCard({ app, active = false, suggestion, condensed = false }: AppCardProps) {
  const { apps, addApp } = useChatContext()

  const isAppSelected = apps.some((a) => a.id === app?.id)
  const primaryCategory = app ? app.primaryCategoryName ?? extractPrimaryCategory(app) : undefined

  const appCardIcon = (app?: AppProps) => {
    if (!app)
      return (
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] border border-gray-200 bg-gray-50 shadow-sm">
          <span className="h-6 w-6 rounded-full border border-gray-200 bg-gray-200"></span>
        </div>
      )

    if (condensed) {
      return (
        <div className="relative h-5 w-5 shrink-0">
          <AppIcon
            icon={app.icon}
            glyph={app.glyph}
            name={app.name}
            background={app.brandBackgroundColor}
            size={20}
            className={cn(
              'absolute right-0 top-0',
              isAppSelected &&
                'origin-center transition-all duration-300 group-hover:scale-0 group-hover:opacity-0',
            )}
          />
          {isAppSelected && (
            <div className="absolute right-0 top-0 flex h-5 w-5 origin-center scale-0 items-center justify-center rounded-full text-gray-500 opacity-0 transition-all duration-300 group-hover:scale-100 group-hover:opacity-100">
              <Icon name="xmark" size={12} />
            </div>
          )}
        </div>
      )
    }

    return (
      <AppIcon
        icon={app.icon}
        glyph={app.glyph}
        name={app.name}
        background={app.brandBackgroundColor}
        size={40}
      />
    )
  }

  const appCardCopy = (app?: AppProps) => {
    const dealCount = determineDealsToDisplay((app?.deals?.docs ?? []) as Deal[]).length
    return (
      <div
        className={cn('flex w-full truncate', condensed ? 'items-center gap-2' : 'flex-col gap-1')}
      >
        <span className="truncate text-14 font-medium leading-tight text-gray-950">
          {suggestion ? 'Your suggestion here' : app?.name}
        </span>
        <span className={cn('h-full text-12 leading-tight text-gray-500', condensed && 'pt-0.5')}>
          {condensed ? (
            primaryCategory
          ) : (
            <>
              App {suggestion ? suggestion : 'review'}, {dealCount} deal
              {dealCount === 1 ? '' : 's'}
            </>
          )}
        </span>
      </div>
    )
  }

  return (
    <div
      onClick={() => app && addApp(app)}
      className={cn(
        'group flex cursor-pointer items-center truncate pl-2 pr-2.5 transition-all',
        condensed
          ? 'h-9 gap-2 rounded-lg bg-gray-100 hover:bg-gray-200'
          : 'h-14 gap-3 rounded-2xl border border-black/10 bg-white/75',
        !condensed
          ? !isAppSelected && active
            ? 'hover:border-primary-200 hover:shadow-[0px_0px_0px_3px_rgba(255,240,242,1.00)]'
            : 'border-primary-200 shadow-[0px_0px_0px_3px_rgba(255,240,242,1.00)]'
          : '',
        suggestion || condensed ? 'w-auto shrink-0' : 'w-56 shrink',
      )}
    >
      {appCardIcon(app)}
      {appCardCopy(app)}

      {!condensed && (
        <div className="relative h-full w-5">
          <div className={cn('absolute right-0 top-2 text-primary-500')}>
            <Icon name="best" />
          </div>
        </div>
      )}
    </div>
  )
}
